import { Box, Button, List, ListItem, Modal, useTheme } from "@mui/material";
import { useState, useEffect } from "react";
import * as React from "react";

import {
  RobotoButton,
  RobotoCheckbox,
  RobotoTypography,
} from "@/shared/components";
import { formatFieldName } from "@/shared/text";

interface ColumnsModalProps {
  open: boolean;
  handleClose: () => void;
  currentColumns: string[];
  standardColumns: string[];
  metadataColumns: string[];
  onSave: (selectedColumns: string[]) => void;
}

export const ColumnsModal: React.FC<ColumnsModalProps> = ({
  open,
  handleClose,
  currentColumns,
  standardColumns,
  metadataColumns,
  onSave,
}) => {
  const theme = useTheme();

  const [selectedColumns, setSelectedColumns] = useState<Set<string>>(
    new Set(currentColumns),
  );

  const INITIAL_METADATA_DISPLAY_COUNT = 25;
  const METADATA_INCREMENT = 25;

  const [visibleMetadataCount, setVisibleMetadataCount] = useState<number>(
    INITIAL_METADATA_DISPLAY_COUNT,
  );

  useEffect(() => {
    if (open) {
      setSelectedColumns(new Set(currentColumns));
      setVisibleMetadataCount(INITIAL_METADATA_DISPLAY_COUNT);
    }
  }, [open, currentColumns]);

  const visibleMetadataColumns = metadataColumns.slice(0, visibleMetadataCount);
  const hasMoreMetadata = visibleMetadataCount < metadataColumns.length;
  const canShowLess = visibleMetadataCount > INITIAL_METADATA_DISPLAY_COUNT;
  const remainingMetadataCount = metadataColumns.length - visibleMetadataCount;
  const showShowAllButton = remainingMetadataCount > METADATA_INCREMENT;

  return (
    <Modal
      open={open}
      onClose={handleClose}
      aria-labelledby="columns-modal-title"
      aria-describedby="columns-modal-description"
    >
      <Box
        sx={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          width: {
            xs: "90%",
            sm: "80%",
            md: "60%",
            lg: "50%",
            xl: "40%",
          },
          maxWidth: "500px",
          bgcolor: "background.paper",
          border: theme.border.thin,
          borderRadius: theme.border.radius,
          boxShadow: 24,
          display: "flex",
          flexDirection: "column",
          maxHeight: "80vh",
        }}
      >
        {/* Header */}
        <Box
          sx={{ padding: theme.spacing(3), paddingBottom: theme.spacing(2) }}
        >
          <RobotoTypography variant={"h6"}>Columns</RobotoTypography>
        </Box>

        {/* Scrollable content area */}
        <Box
          sx={{
            flex: 1,
            overflowY: "auto",
            paddingX: theme.spacing(3),
            paddingBottom: theme.spacing(2),
          }}
        >
          <List>
            {/* Render standard columns */}
            {standardColumns.map((column) => {
              return (
                <ListItem key={column} dense sx={{ padding: 0 }}>
                  <RobotoCheckbox
                    size="small"
                    checked={selectedColumns.has(column)}
                    onChange={(e) => {
                      if (e.target.checked) {
                        const newColumns = new Set(selectedColumns);
                        newColumns.add(column);
                        setSelectedColumns(newColumns);
                      } else {
                        const newColumns = new Set(selectedColumns);
                        newColumns.delete(column);
                        setSelectedColumns(newColumns);
                      }
                    }}
                  />
                  <RobotoTypography variant={"body2"}>
                    {formatFieldName(column)}
                  </RobotoTypography>
                </ListItem>
              );
            })}

            {/* Render visible metadata columns */}
            {visibleMetadataColumns.map((column) => {
              return (
                <ListItem key={column} dense sx={{ padding: 0 }}>
                  <RobotoCheckbox
                    size="small"
                    checked={selectedColumns.has(column)}
                    onChange={(e) => {
                      if (e.target.checked) {
                        const newColumns = new Set(selectedColumns);
                        newColumns.add(column);
                        setSelectedColumns(newColumns);
                      } else {
                        const newColumns = new Set(selectedColumns);
                        newColumns.delete(column);
                        setSelectedColumns(newColumns);
                      }
                    }}
                  />
                  <RobotoTypography variant={"body2"}>
                    {formatFieldName(column)}
                  </RobotoTypography>
                </ListItem>
              );
            })}
          </List>

          {/* Show More / Show All / Show Less buttons for metadata columns */}
          {metadataColumns.length > 0 && (
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                gap: theme.spacing(1),
                mt: theme.spacing(1),
                mb: theme.spacing(1),
              }}
            >
              {hasMoreMetadata && (
                <>
                  <Button
                    variant={"text"}
                    size="small"
                    onClick={() => {
                      setVisibleMetadataCount((prev) =>
                        Math.min(
                          prev + METADATA_INCREMENT,
                          metadataColumns.length,
                        ),
                      );
                    }}
                  >
                    Show More
                  </Button>
                  {showShowAllButton && (
                    <Button
                      variant={"text"}
                      size="small"
                      onClick={() => {
                        setVisibleMetadataCount(metadataColumns.length);
                      }}
                    >
                      Show All
                    </Button>
                  )}
                </>
              )}
              {canShowLess && (
                <Button
                  variant={"text"}
                  size="small"
                  onClick={() => {
                    setVisibleMetadataCount(INITIAL_METADATA_DISPLAY_COUNT);
                  }}
                >
                  Show Less
                </Button>
              )}
            </Box>
          )}
        </Box>

        {/* Sticky footer with clear separation */}
        <Box
          sx={{
            borderTop: theme.border.thin,
            padding: theme.spacing(2),
            paddingX: theme.spacing(3),
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            flexDirection: {
              xs: "column",
              sm: "row",
            },
            gap: theme.spacing(2),
            bgcolor: "background.paper",
          }}
        >
          <RobotoButton
            eventName={"UpdateDatasetTableColumnsFormSubmitted"}
            variant={"contained"}
            sx={{
              width: "100px",
            }}
            onClick={() => {
              const columnsToSave = Array.from(selectedColumns.keys());
              onSave(columnsToSave);
            }}
          >
            Update
          </RobotoButton>
          <RobotoButton
            eventName={"UpdateDatasetTableColumnsFormCanceled"}
            variant={"outlined"}
            onClick={() => {
              handleClose();
            }}
            sx={{ width: "100px" }}
          >
            Cancel
          </RobotoButton>
        </Box>
      </Box>
    </Modal>
  );
};
